/**
 * Web-side offline-sync hook — a thin wrapper around
 * `runOfflineSync` from `@splitfiesta/core` that binds the platform-
 * specific API caller (`useTypedApi`) and the React Query cache
 * invalidation callbacks. Auto-fires sync 2 s after coming back
 * online with pending expenses queued, then keeps retrying with
 * exponential backoff (`nextSyncRetryDelayMs`) while retryable items
 * remain: a backend outage leaves `isOnline` true, so nothing else would
 * re-trigger a flush until a reload.
 *
 * The orchestration (retry-with-backoff, status transitions,
 * sync-time cloud category upgrade) lives in core so mobile shares
 * the same code path — see apps/mobile/src/hooks/useOfflineSync.ts.
 */
import { useShallow } from 'zustand/react/shallow';

import { useCallback, useEffect, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import {
  EXPENSE_TAXONOMY_VERSION,
  checkIsOnline,
  nextSyncRetryDelayMs,
  offlineExpenseService,
  queryKeys,
  runOfflineSync,
  selectOwnedExpenses,
  userPro,
} from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';
import type {
  ExchangeRatePreviewDto,
  PredictCategoryResponseDto,
  SuccessResponseDto,
  SyncResult,
} from '@splitfiesta/shared';

import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useTypedApi } from '@/hooks/useTypedApi';

import { useAuthStore } from '@/stores/authStore';
import { useOfflineStore } from '@/stores/offlineStore';

const NOTHING_SYNCED: SyncResult = {
  success: false,
  syncedCount: 0,
  failedCount: 0,
  errors: [],
};

const SYNC_LOCK_NAME = 'splitfiesta-offline-sync';

/**
 * Runs `run` only while no other tab of this origin is syncing; the queue is
 * shared through localStorage, so two tabs would POST the same rows. Without
 * the Locks API the run is unguarded, as before.
 */
async function withCrossTabSyncLock(
  run: () => Promise<SyncResult>
): Promise<SyncResult> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks) return run();
  return locks.request(SYNC_LOCK_NAME, { ifAvailable: true }, lock =>
    lock ? run() : NOTHING_SYNCED
  );
}

/** Count of `ownerId`'s queued expenses still worth attempting. */
function retryableCount(ownerId: string | undefined): number {
  return offlineExpenseService.getRetryableExpenses(
    selectOwnedExpenses(useOfflineStore.getState(), ownerId)
  ).length;
}

export function useOfflineSync() {
  const api = useTypedApi();
  const queryClient = useQueryClient();
  const { isOnline } = useNetworkStatus();
  const { i18n } = useTranslation();

  // Cloud cache keys on the full BCP-47 locale tag so web hits the same
  // rows mobile populated (see notes in useCategoryPrediction.ts). Use
  // `navigator.language` when available, fall back to i18n's UI language.
  const locale =
    typeof navigator !== 'undefined' && navigator.language
      ? navigator.language
      : i18n.language;

  const ownerId = useAuthStore(state =>
    state.isAuthenticated ? state.user?.id : undefined
  );
  const isPro = useAuthStore(
    state => state.isAuthenticated && userPro(state.user).active
  );
  const hasHydrated = useOfflineStore(state => state.hasHydrated);
  const pendingExpenses = useOfflineStore(
    useShallow(state => selectOwnedExpenses(state, ownerId))
  );
  const isSyncing = useOfflineStore(state => state.isSyncing);
  const retryRequestedAt = useOfflineStore(state => state.retryRequestedAt);

  // Rows queued before owners were recorded go to whoever signs in first.
  // Waits for hydration, which would otherwise overwrite the stamps.
  useEffect(() => {
    if (ownerId && hasHydrated) {
      useOfflineStore.getState().adoptUnownedExpenses(ownerId);
    }
  }, [ownerId, hasHydrated]);

  // The caller's own Pro lifts the free group limit everywhere, so rows it
  // refused would otherwise sit failed until edited.
  useEffect(() => {
    if (ownerId && hasHydrated && isPro) {
      useOfflineStore.getState().requeueGroupLimitFailures(ownerId);
    }
  }, [ownerId, hasHydrated, isPro]);

  const syncAllPending = useCallback(async (): Promise<SyncResult> => {
    // Signed out, every POST would 401 and spend the rows' retry budget.
    if (!ownerId) return NOTHING_SYNCED;
    return withCrossTabSyncLock(() =>
      runOfflineSync({
        store: useOfflineStore.getState(),
        isOnline,
        ownerId,
        createExpense: data => api.expenses.create(data),
        updateExpense: (id, data) => api.expenses.update(id, data),
        getExchangeRate: async ({ from, to, date }) => {
          const response = (await api.exchangeRates.get({
            from,
            to,
            date,
          })) as SuccessResponseDto<ExchangeRatePreviewDto>;
          return { rate: response.data.rate, rateDate: response.data.rateDate };
        },
        predictCategory: async input => {
          try {
            const response = (await api.expenses.predictCategory({
              ...input,
              taxonomyVersion: EXPENSE_TAXONOMY_VERSION,
            })) as SuccessResponseDto<PredictCategoryResponseDto>;
            return {
              category: response.data.category,
              subcategory: response.data.subcategory,
              confidence: response.data.confidence,
              taxonomyVersion: response.data.taxonomyVersion,
            };
          } catch {
            // 502 invalid prediction / 503 not configured or degraded /
            // network — skip the upgrade gracefully.
            return null;
          }
        },
        locale,
        onGroupSynced: async groupId => {
          await queryClient.invalidateQueries({
            queryKey: queryKeys.expensesByGroup(groupId),
          });
          await queryClient.invalidateQueries({
            queryKey: queryKeys.groupBalances(groupId),
          });
          await queryClient.invalidateQueries({
            queryKey: queryKeys.groupWithDashboard(groupId),
          });
          await queryClient.invalidateQueries({
            queryKey: [...queryKeys.all, 'activity', 'feed'],
          });
          await queryClient.invalidateQueries({
            queryKey: [...queryKeys.groups(), 'list'],
          });
        },
      })
    );
  }, [api, isOnline, locale, ownerId, queryClient]);

  // --- Outbox flush loop -------------------------------------------------
  // Mirrors apps/mobile/src/hooks/useOfflineSync.ts: a single
  // self-rescheduling timer, started 2 s after queued work appears while
  // online (the delay smooths flapping networks). Each tick flushes and,
  // while retryable items remain, reschedules with exponential backoff; it
  // stops when the queue drains, the browser goes offline or the user signs
  // out. A tick that lands while a run is in flight (`runOfflineSync` would
  // no-op) is parked in `rearmRef` and replayed once that run ends.
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const attemptRef = useRef(0);
  const loopActiveRef = useRef(false);
  const rearmRef = useRef(false);
  // Real implementation assigned in the effect below before the first
  // tick can fire (the earliest schedule is 2 s out).
  const tickRef = useRef<() => Promise<void>>(async () => undefined);

  const schedule = useCallback((delayMs: number) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      void tickRef.current();
    }, delayMs);
  }, []);

  const stopLoop = useCallback(() => {
    loopActiveRef.current = false;
    rearmRef.current = false;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    attemptRef.current = 0;
  }, []);

  useEffect(() => {
    tickRef.current = async () => {
      timerRef.current = null;
      if (!loopActiveRef.current) return;
      if (!checkIsOnline()) {
        stopLoop();
        return;
      }
      if (useOfflineStore.getState().isSyncing) {
        rearmRef.current = true;
        return;
      }
      await syncAllPending();
      if (!loopActiveRef.current) return; // torn down mid-flush
      if (timerRef.current || rearmRef.current) return; // an edit re-armed it
      if (retryableCount(ownerId) > 0 && checkIsOnline()) {
        attemptRef.current += 1;
        schedule(nextSyncRetryDelayMs(attemptRef.current));
      } else {
        stopLoop();
      }
    };
  }, [ownerId, syncAllPending, stopLoop, schedule]);

  const startLoop = useCallback(
    (initialDelayMs: number) => {
      if (loopActiveRef.current) return;
      loopActiveRef.current = true;
      attemptRef.current = 0;
      schedule(initialDelayMs);
    },
    [schedule]
  );

  // An edit of a failed item is the user's explicit retry: if the loop is
  // sleeping in backoff (minutes), drop that timer and tick in 2 s instead.
  const restartLoop = useCallback(
    (delayMs: number) => {
      if (!loopActiveRef.current) {
        startLoop(delayMs);
        return;
      }
      attemptRef.current = 0;
      schedule(delayMs);
    },
    [schedule, startLoop]
  );

  // `pendingExpenses` is already the signed-in user's, so it is empty while
  // signed out.
  useEffect(() => {
    if (isOnline && pendingExpenses.length > 0) startLoop(2000);
  }, [isOnline, pendingExpenses.length, startLoop]);

  useEffect(() => {
    if (!isOnline || !ownerId) stopLoop();
  }, [isOnline, ownerId, stopLoop]);

  // Same queue length after an edit, so the stamp is the only signal.
  const lastRetryRef = useRef(retryRequestedAt);
  useEffect(() => {
    if (retryRequestedAt === lastRetryRef.current) return;
    lastRetryRef.current = retryRequestedAt;
    if (isOnline && pendingExpenses.length > 0) restartLoop(2000);
  }, [retryRequestedAt, isOnline, pendingExpenses.length, restartLoop]);

  useEffect(() => {
    if (isSyncing || !rearmRef.current) return;
    rearmRef.current = false;
    void tickRef.current();
  }, [isSyncing]);

  useEffect(() => stopLoop, [stopLoop]);

  return {
    syncAllPending,
    isSyncing,
    pendingCount: pendingExpenses.filter(e => e.syncStatus === 'pending')
      .length,
    failedCount: pendingExpenses.filter(e => e.syncStatus === 'failed').length,
    totalPending: pendingExpenses.length,
    isOnline,
  };
}
